import { ArrowDown, BatteryCharging, Camera, Check, MapPin, PackageCheck, ScanSearch, ShieldCheck, Smartphone, Wrench } from "lucide-react"
import { motion } from "framer-motion"
import { AmbientBackground } from "./components/experience/AmbientBackground"
import { PhoneStage } from "./components/experience/PhoneStage"
import { StoryProgress } from "./components/experience/StoryProgress"
import { Header } from "./components/sections/Header"
import { ServiceSection } from "./components/sections/ServiceSection"
import { Button } from "./components/ui/Button"

const products = ["Celulares", "Relógios digitais", "Mini ventiladores", "Copos térmicos"]

function App() {
  return (
    <div className="app-shell">
      <a className="skip-link" href="#conteudo">Pular para o conteúdo</a>
      <AmbientBackground />
      <Header />

      <main id="conteudo">
        <div className="story">
          <PhoneStage />
          <StoryProgress />
          <section className="hero" id="inicio">
            <motion.div className="hero__copy" initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.75, delay: 0.15 }}>
              <p className="eyebrow"><span /> Especialistas em reparo de celulares</p>
              <h1>Seu celular.<br /><em>Inteiro de novo.</em></h1>
              <p className="hero__lede">Diagnóstico e reparo com atenção a cada detalhe — do vidro ao conector.</p>
              <div className="hero__actions">
                <Button href="#contato">Solicitar orçamento</Button>
                <a className="text-link" href="#servicos">Conheça os reparos <ArrowDown size={15} /></a>
              </div>
            </motion.div>
            <div className="hero__aside">
              <span>Atendimento presencial</span>
              <strong>Shopping Negreiros</strong>
            </div>
            <div className="scroll-cue" aria-hidden="true"><span>Role para explorar</span><i /></div>
          </section>

          <div id="servicos" className="services" aria-label="Serviços de reparo">
            <ServiceSection index="01" eyebrow="Tela" title="Clareza de volta ao toque." description="Tela trincada, falhas de imagem ou toque sem resposta? Avaliamos o aparelho para indicar o reparo adequado." detail="Inspeção de vidro, imagem e sensibilidade ao toque." icon={Smartphone} side="left" />
            <ServiceSection index="02" eyebrow="Câmeras" title="Volte a registrar cada detalhe." description="Investigamos falhas de foco, lentes danificadas e problemas que comprometem suas fotos e vídeos." detail="Avaliação das câmeras e do conjunto de lentes." icon={Camera} side="right" />
            <ServiceSection index="03" eyebrow="Estrutura" title="Botões, laterais e conexão." description="Pequenos componentes fazem toda a diferença no uso diário. Identificamos a origem da falha antes do reparo." detail="Conectores, botões e integridade da estrutura." icon={Wrench} side="left" />
            <ServiceSection index="04" eyebrow="Bateria" title="Energia para acompanhar seu ritmo." description="Se a carga não dura ou o aparelho desliga sozinho, um diagnóstico cuidadoso aponta o próximo passo." detail="Análise de bateria, carga e consumo do aparelho." icon={BatteryCharging} side="right" />
          </div>

          <section className="process" id="sobre">
            <motion.div className="process__intro" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.35 }}>
              <p className="eyebrow"><span /> Cuidado do início ao fim</p>
              <h2>Primeiro entender.<br />Depois, reparar.</h2>
            </motion.div>
            <div className="process__steps">
              <article><span>01</span><ScanSearch /><h3>Diagnóstico</h3><p>O aparelho é avaliado para identificar a causa do problema.</p></article>
              <article><span>02</span><ShieldCheck /><h3>Orientação clara</h3><p>Você entende a necessidade do reparo antes de decidir.</p></article>
              <article><span>03</span><PackageCheck /><h3>Cuidado técnico</h3><p>O serviço é tratado com atenção aos componentes e ao acabamento.</p></article>
            </div>
            <p className="process__note">Prazos, condições e garantia são informados conforme a avaliação do aparelho.</p>
          </section>
        </div>

        <section className="extras">
          <div className="extras__copy">
            <p className="eyebrow"><span /> Também na Equipe Cell</p>
            <h2>Tecnologia e praticidade, escolhidas para o seu dia.</h2>
            <p>Além dos reparos, você encontra uma seleção de produtos. Consulte a disponibilidade diretamente na loja.</p>
          </div>
          <div className="product-list">
            {products.map((product, index) => (
              <div key={product}><span>0{index + 1}</span><strong>{product}</strong><Check size={18} /></div>
            ))}
          </div>
        </section>

        <section className="location" id="localizacao">
          <div className="location__visual" aria-hidden="true">
            <div className="location__grid" />
            <MapPin size={36} strokeWidth={1.3} />
            <span>23.59° S · 46.40° W</span>
          </div>
          <div className="location__copy">
            <p className="eyebrow"><span /> Venha conversar com a gente</p>
            <h2>Seu próximo reparo começa aqui.</h2>
            <address><strong>Shopping Negreiros</strong><br />Av. Ragueb Chohfi, 4831</address>
            <p className="provisional">Endereço preliminar. Confirme detalhes da loja e horário antes da visita.</p>
            <Button href="https://www.google.com/maps/dir/?api=1&destination=Shopping+Negreiros+Av.+Ragueb+Chohfi+4831" target="_blank" rel="noreferrer">Abrir rota</Button>
          </div>
        </section>

        <section className="final-cta" id="contato">
          <div className="final-cta__topline"><span>Equipe Cell</span><span>Reparo de celulares</span></div>
          <h2>Pronto para cuidar<br />do seu celular?</h2>
          <p>Visite a Equipe Cell no Shopping Negreiros para avaliar seu aparelho e conversar sobre o reparo.</p>
          <Button href="#localizacao">Ver localização</Button>
        </section>
      </main>

      <footer>
        <div className="footer__brand"><img src="/logo equipe cell sem fundo.png" alt="Equipe Cell" /><p>Reparo de celulares e produtos selecionados para tecnologia e praticidade.</p></div>
        <div><span>Localização</span><p>Shopping Negreiros<br />Av. Ragueb Chohfi, 4831</p></div>
        <div><span>Créditos do modelo 3D</span><p><a href="https://skfb.ly/pCnq8" target="_blank" rel="noreferrer">“iPhone 17 Pro”</a> por Ranguel, licenciado sob <a href="http://creativecommons.org/licenses/by/4.0/" target="_blank" rel="noreferrer">Creative Commons Attribution</a>.</p></div>
        <div className="footer__bottom"><span>© {new Date().getFullYear()} Equipe Cell</span><span>Apple é uma marca de seus respectivos proprietários. Não há vínculo oficial.</span></div>
      </footer>
    </div>
  )
}

export default App
